import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ContactEditor, holdsBoth, useOwnContact } from "../../components/ContactFields.js";
import { Button, inputStyle, Notice } from "../../components/ui.js";
import { useAuth } from "../../lib/auth.js";
import { enterableFor, fetchInvitedIds } from "../../lib/invites.js";
import { addManagedMember, fetchMyChildren } from "../../lib/members.js";
import {
  registerForEvent,
  updateRegistrationNote,
  withdrawRegistration,
} from "../../lib/tournaments.js";
import {
  areEntriesClosed,
  entryRefusal,
  EVENT_CATEGORY_LABEL,
  eventGender,
  isKidsCategory,
  ordinal,
  refusalIsPlayingAs,
  waitlistPosition,
  type Member,
  type MemberCategory,
  type Tournament,
  type TournamentEvent,
} from "../../lib/types.js";
import { EventSummary } from "../../components/EventSummary.js";
import { EventGate, EventScreen, useEventNotes } from "./shared.js";

const NOTE_LIMIT = 280;

export const closesWhen = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });

/**
 * Getting in, getting out, and saying the thing about your week.
 *
 * Its own screen because it is the one people arrive at from a WhatsApp link and the one
 * they never open again after. Sharing the page with the draw meant a first-time visitor had
 * to work out which of six things on it was the way in.
 */
export function EventRegister() {
  return (
    <EventGate>
      {({ tournament, event }) => <Register tournament={tournament} event={event} />}
    </EventGate>
  );
}

/**
 * You, then each child you look after — whoever of you this event is for — each with their own
 * entry.
 *
 * One row per person rather than a "who is this for" picker, because a parent's real question
 * is "who of us is in", and a picker can only ever show one answer to it. Every row goes
 * through the same RPCs with `on_behalf_of` set, so a child meets exactly the rules they would
 * meet signing up themselves — see 20260929000100.
 *
 * Only people the event's category lets in are listed (20260930000100), decided by
 * `entryRefusal`, the mirror of the database's rule. Somebody already entered stays listed even
 * if they no longer qualify — an organiser may have put them in — so they can still be taken out.
 * A kids' event with nobody to list is where a parent meets it for the first time, so it says
 * who the event is for and adds a child right here, boy or girl already set. A junior on an
 * adult event is told where they can play instead (20260930000200).
 *
 * A tournament that asks for contact details asks here, above the rows, before Register does
 * anything — and asks for yours even when you are only entering a child, because a child holds
 * none and the database checks whoever is entering (20261001000300). Saving goes to your
 * profile, through the same query this reads, so the ask disappears the moment it is answered.
 */
function Register({ tournament, event }: { tournament: Tournament; event: TournamentEvent }) {
  const { member } = useAuth();
  const memberId = member?.id ?? null;
  const [adding, setAdding] = useState(false);

  const { data: notes } = useEventNotes(event.id, Boolean(memberId));
  const { data: children = [] } = useQuery({
    queryKey: ["children", memberId],
    queryFn: () => fetchMyChildren(memberId!),
    enabled: Boolean(memberId),
  });

  // On a private tournament, who of yours is invited: your own row and your children's, which
  // is all a member may read of the list. Not asked for a public one.
  const invites = useQuery({
    queryKey: ["tournament-invites", tournament.id],
    queryFn: () => fetchInvitedIds(tournament.id),
    enabled: tournament.private && Boolean(memberId),
  });

  const closed = areEntriesClosed(event);
  const entered = (id: string) => event.entries.some((entry) => entry.memberId === id);
  const shown = (person: Member) =>
    entered(person.id) || entryRefusal(event.category, person) === null;

  const people = enterableFor(
    [
      ...(member && shown(member) ? [{ id: member.id, childName: null as string | null }] : []),
      ...children.filter(shown).map((child) => ({ id: child.id, childName: child.displayName })),
    ],
    tournament,
    invites.data ?? [],
    entered,
  );
  const inviteOnly = tournament.private && people.length === 0 && invites.isSuccess;
  const alone = people.length === 1 && people[0]!.childName === null;
  const kids = isKidsCategory(event.category);
  const gender = eventGender(event.category);
  const noun = gender === "men" ? "boy" : "girl";
  const ownRefusal = member ? entryRefusal(event.category, member) : null;

  const { data: ownContact } = useOwnContact(memberId);
  const someoneToEnter = people.some((person) => !entered(person.id));
  // Until your details are known, Register waits rather than offering a tap that may be refused.
  const blocked = tournament.contactRequired && !holdsBoth(ownContact);
  const askContact =
    blocked && ownContact !== undefined && memberId !== null && !closed && someoneToEnter;

  // Where a junior refused from an adult event plays instead: the kids' events of their own
  // category, or of the event's when theirs is not set. Read off the member, not the refusal's
  // words, and linked when this tournament has any.
  const kidsInstead =
    member?.junior && !kids && gender
      ? (member.category ?? gender) === "men"
        ? "boys"
        : "girls"
      : null;
  const kidsEvents = kidsInstead
    ? tournament.events.filter((other) => other.category === kidsInstead)
    : [];

  // Until the list is known, nothing is offered, rather than offering people the database
  // would then refuse.
  if (tournament.private && memberId && invites.isPending) {
    return (
      <EventScreen event={event} title="Register">
        <p className="micro">Loading…</p>
      </EventScreen>
    );
  }

  return (
    <EventScreen
      event={event}
      // "Register" until somebody is in. "Entries" over a page that entered nobody — and, for
      // a senior-only event, could enter nobody — named a list that wasn't there.
      title={
        people.some((person) => entered(person.id))
          ? alone
            ? "Your entry"
            : "Entries"
          : "Register"
      }
    >
      <div className="mb-7">
        <EventSummary event={event} />
      </div>

      <Requirements
        event={event}
        tournament={tournament}
        kids={kids}
        ownRefusal={ownRefusal}
        contactOnFile={holdsBoth(ownContact)}
        contactKnown={ownContact !== undefined}
        contactFormBelow={askContact}
        closed={closed}
      />

      {askContact ? (
        <section className="mb-6 border-t-2 pt-4" style={{ borderColor: "var(--color-line)" }}>
          <div className="micro mb-3">Your contact details</div>
          <p className="mb-4 text-sm">
            {tournament.name} needs your flat number and phone number before{" "}
            {people.some((person) => person.childName === null && !entered(person.id))
              ? "you can enter"
              : "you can enter a child"}
            . Only organisers and admins see them, and they&rsquo;re saved to{" "}
            <Link to="/me?tab=details#contact" className="underline">
              your profile
            </Link>
            .
          </p>
          <ContactEditor memberId={memberId} needBoth />
        </section>
      ) : null}

      {people.map((person) => (
        <PersonEntry
          key={person.id}
          event={event}
          personId={person.id}
          childName={person.childName}
          savedNote={notes?.get(person.id) ?? null}
          alone={alone}
          blocked={blocked}
        />
      ))}

      {inviteOnly ? (
        <p className="mb-4">
          {tournament.name} is invite only, and nobody you can enter has been invited. Ask an
          organiser to invite you
          {children.length > 0 ? " or your child" : ""}.
        </p>
      ) : null}

      {people.length === 0 && kids && !inviteOnly ? (
        <p className="mb-4">
          {event.name} is for {gender === "men" ? "boys" : "girls"}. You haven&rsquo;t added a{" "}
          {noun} yet.
        </p>
      ) : null}

      {/* Why you can't enter is the checklist's first row now, with its fix beside it. What
          stays here is the one thing a row can't say: where a junior plays instead. */}
      {people.length === 0 && !kids && ownRefusal && kidsInstead ? (
        <p className="mb-4 text-[15px]">
          {kidsInstead ? (
            <>
              Juniors play in{" "}
              {kidsEvents.length === 0
                ? `${kidsInstead}' events`
                : kidsEvents.map((other, i) => (
                    <span key={other.id}>
                      {i === 0 ? "" : i === kidsEvents.length - 1 ? " and " : ", "}
                      <Link to={`/events/${other.id}/register`} className="underline">
                        {other.name}
                      </Link>
                    </span>
                  ))}
              .
            </>
          ) : null}
        </p>
      ) : null}

      {/* The way in for a child who is not on the list yet. Open at once when there is nobody
          to enter, and one tap away otherwise. */}
      {kids && memberId && !closed ? (
        people.length === 0 || adding ? (
          <AddChild
            guardianId={memberId}
            gender={gender!}
            noun={noun}
            onAdded={() => setAdding(false)}
            onCancel={people.length === 0 ? undefined : () => setAdding(false)}
          />
        ) : (
          <button type="button" className="micro underline" onClick={() => setAdding(true)}>
            Not listed? Add a child
          </button>
        )
      ) : null}
    </EventScreen>
  );
}

/**
 * What entering takes, one row per condition, each with its state and — where it is yours to
 * fix — the fix on the same row. These used to be four separate lines in four styles (a grey
 * caption about contact details, a closing time in caps, the refusal as a paragraph), and
 * whether you could enter at all was the last thing you found out.
 *
 * Only the conditions this event has are listed. A kids' event has no "who can enter" row:
 * the grown-up is not the entrant, and the add-a-child form below is the answer to it.
 */
function Requirements({
  event,
  tournament,
  kids,
  ownRefusal,
  contactOnFile,
  contactKnown,
  contactFormBelow,
  closed,
}: {
  event: TournamentEvent;
  tournament: Tournament;
  kids: boolean;
  ownRefusal: string | null;
  contactOnFile: boolean;
  contactKnown: boolean;
  /** Whether the form is on this screen. It isn't when nobody here can enter yet. */
  contactFormBelow: boolean;
  closed: boolean;
}) {
  const restricted = event.category !== "open" && event.category !== "mixed";
  const fixable =
    ownRefusal !== null && (ownRefusal.startsWith("a category") || refusalIsPlayingAs(ownRefusal));

  const rows: {
    label: string;
    ok: boolean;
    detail: string;
    fix?: { to: string; label: string };
  }[] = [];

  if (restricted && !kids) {
    rows.push({
      label: `${EVENT_CATEGORY_LABEL[event.category]} only`,
      ok: ownRefusal === null,
      detail: ownRefusal === null ? "You can enter" : sentence(ownRefusal),
      fix: fixable ? { to: "/me?tab=details#about", label: "Change" } : undefined,
    });
  }
  if (tournament.contactRequired && contactKnown) {
    rows.push({
      label: "Flat and phone number",
      ok: contactOnFile,
      detail: contactOnFile
        ? "On file"
        : contactFormBelow
          ? "Needed — fill them in below"
          : "Needed — only organisers and admins see them",
      fix:
        contactOnFile || contactFormBelow
          ? undefined
          : { to: "/me?tab=details#contact", label: "Add" },
    });
  }
  if (event.entriesCloseAt) {
    rows.push({
      label: "Entries",
      ok: !closed,
      detail: `${closed ? "Closed" : "Open until"} ${closesWhen(event.entriesCloseAt)}`,
    });
  }

  if (rows.length === 0) return null;

  return (
    <section aria-label="To enter" className="mb-7">
      <h2 className="font-mono mb-2 text-[11px] font-semibold uppercase tracking-[0.12em]">
        To enter
      </h2>
      <ul className="border-t-2" style={{ borderColor: "var(--color-line)" }}>
        {rows.map((row) => (
          <li
            key={row.label}
            className="flex min-h-[56px] items-center gap-3 border-b py-2"
            style={{ borderColor: "var(--color-line-muted)" }}
          >
            {/* Shape as well as colour: a filled tick, or an orange-ringed cross — orange is
                "waiting on you", which an unmet condition is. */}
            <span
              aria-hidden
              className="grid size-6 shrink-0 place-items-center text-xs font-bold"
              style={
                row.ok
                  ? { background: "var(--color-line)", color: "var(--color-ground)" }
                  : {
                      border: "2px solid var(--color-attention)",
                      color: "var(--color-attention)",
                    }
              }
            >
              {row.ok ? "✓" : "✕"}
            </span>
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="text-[15px] font-semibold">
                <span className="sr-only">{row.ok ? "Met: " : "Not met: "}</span>
                {row.label}
              </span>
              <span className="text-sm text-ink-muted">{row.detail}</span>
            </span>
            {row.fix ? (
              <Link
                to={row.fix.to}
                className="flex min-h-[44px] shrink-0 items-center border-[1.5px] px-3 text-sm font-semibold"
                style={{ borderColor: "var(--color-line)" }}
              >
                {row.fix.label}
              </Link>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

/** "this event is for men" -> "This event is for men". The database's words, as a sentence. */
const sentence = (fragment: string) => fragment.charAt(0).toUpperCase() + fragment.slice(1);

/**
 * Adding a child from the register screen of a kids' event.
 *
 * Boy or girl is not asked: the event has already said, and a child added here is added to be
 * entered here. The new child arrives through the same `children` query the rows come from, so
 * they appear ready to Enter without anything else refetching.
 */
function AddChild({
  guardianId,
  gender,
  noun,
  onAdded,
  onCancel,
}: {
  /** You: a child added here is yours, whatever your role — see Me's YourChildren. */
  guardianId: string;
  gender: MemberCategory;
  noun: string;
  onAdded: () => void;
  onCancel?: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");

  const add = useMutation({
    mutationFn: () => addManagedMember(name, gender, guardianId),
    onSuccess: () => {
      setName("");
      void queryClient.invalidateQueries({ queryKey: ["children"] });
      void queryClient.invalidateQueries({ queryKey: ["members"] });
      onAdded();
    },
  });

  return (
    <section className="mt-2 border-t-2 pt-4" style={{ borderColor: "var(--color-line)" }}>
      <div className="micro mb-3">Add a {noun}</div>
      <form
        className="flex items-center gap-2"
        onSubmit={(formEvent) => {
          formEvent.preventDefault();
          add.mutate();
        }}
      >
        <input
          value={name}
          onChange={(changeEvent) => setName(changeEvent.target.value)}
          maxLength={60}
          placeholder="Child's name"
          aria-label="Child's name"
          style={inputStyle}
        />
        <Button
          tone="primary"
          type="submit"
          className="shrink-0 whitespace-nowrap"
          disabled={add.isPending || !name.trim()}
        >
          {add.isPending ? "Adding…" : "Add child"}
        </Button>
        {onCancel ? (
          <Button tone="quiet" type="button" className="shrink-0" onClick={onCancel}>
            Cancel
          </Button>
        ) : null}
      </form>
      <p className="mt-1.5 text-sm text-ink-muted">
        Only their name, and that they&rsquo;re a {noun}, is kept. You can then enter them here and
        in other events for them.
      </p>
      {add.error ? <Notice tone="attention">{add.error.message}</Notice> : null}
    </section>
  );
}

/**
 * One person's entry: in, waiting, or not yet, and the controls for whichever it is.
 *
 * `childName` is null for yourself. Everything a child's row says is the same sentence with
 * their name in it, and everything it does is the same call with their id in it.
 */
function PersonEntry({
  event,
  personId,
  childName,
  savedNote,
  alone,
  blocked,
}: {
  event: TournamentEvent;
  personId: string;
  childName: string | null;
  savedNote: string | null;
  /** The only row on the screen, so a successful entry can take you back to the event. */
  alone: boolean;
  /** The tournament wants your contact details first. Entering waits; leaving never does. */
  blocked: boolean;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const onBehalfOf = childName === null ? null : personId;
  const who = childName ?? "You";

  const [note, setNote] = useState("");
  const [editing, setEditing] = useState(false);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["tournaments"] });
    void queryClient.invalidateQueries({ queryKey: ["event-notes", event.id] });
    // An organiser entering themselves or their child reads contacts on the entry list.
    void queryClient.invalidateQueries({ queryKey: ["event-contacts", event.id] });
  };

  const join = useMutation({
    mutationFn: () => registerForEvent(event.id, note, onBehalfOf),
    onSuccess: () => {
      setNote("");
      refresh();
    },
  });
  const leave = useMutation({
    mutationFn: () => withdrawRegistration(event.id, onBehalfOf),
    onSuccess: refresh,
  });
  const saveNote = useMutation({
    mutationFn: (next: string | null) => updateRegistrationNote(event.id, next, onBehalfOf),
    onSuccess: () => {
      setEditing(false);
      refresh();
    },
  });

  const mine = event.entries.find((entry) => entry.memberId === personId);
  const busy = join.isPending || leave.isPending || saveNote.isPending;
  const failure = join.error ?? leave.error ?? saveNote.error;
  const queuePlace = waitlistPosition(event.entries, personId);
  const closed = areEntriesClosed(event);
  const myNote = savedNote;

  return (
    <section
      className={alone ? undefined : "mb-6 border-t-2 pt-4"}
      style={alone ? undefined : { borderColor: "var(--color-line)" }}
    >
      {alone ? null : <div className="micro mb-3">{who}</div>}

      {mine ? (
        <>
          <p>
            {mine.status === "registered"
              ? childName
                ? `${childName} is in.`
                : "You're in."
              : `${childName ? `${childName} is` : "You're"} ${ordinal(queuePlace ?? 0)} in the queue. If somebody drops out, the longest wait goes first.`}
          </p>

          {/* The note stays editable after the cutoff on purpose: "I'll be twenty minutes
              late" is worth more to an organiser the closer it gets to the night. */}
          {closed ? (
            <p className="mt-3 text-sm text-ink-muted">
              {childName
                ? `Entries have closed, so you can no longer take ${childName} out. Tell an organiser if they cannot make it`
                : "Entries have closed, so you can no longer take yourself out. Tell an organiser if you cannot make it"}{" "}
              — leaving a hole in the draw is the thing this avoids.
            </p>
          ) : null}

          {editing ? (
            <div className="mt-4">
              <NoteField value={note} onChange={setNote} forChild={childName !== null} />
              <div className="flex flex-wrap gap-2">
                <Button tone="primary" onClick={() => saveNote.mutate(note)} disabled={busy}>
                  {busy ? "Saving…" : "Save note"}
                </Button>
                <Button onClick={() => setEditing(false)} disabled={busy}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <>
              {myNote ? <p className="mt-3 text-ink-muted">&ldquo;{myNote}&rdquo;</p> : null}
              <div className="mt-6 flex flex-wrap gap-2">
                <Button
                  onClick={() => {
                    setNote(myNote ?? "");
                    setEditing(true);
                  }}
                  disabled={busy}
                >
                  {myNote ? "Edit note" : "Add a note"}
                </Button>
                {closed ? null : (
                  <Button onClick={() => leave.mutate()} disabled={busy}>
                    {leave.isPending ? "Withdrawing…" : "Withdraw"}
                  </Button>
                )}
              </div>
            </>
          )}
        </>
      ) : closed ? (
        <p>
          Entries for this event have closed. An organiser can still put {childName ?? "you"} in.
        </p>
      ) : (
        <>
          <NoteField value={note} onChange={setNote} forChild={childName !== null} />
          <Button
            tone="primary"
            onClick={() =>
              join.mutate(undefined, {
                // Back to the event when you were the only one to enter. With children on the
                // screen, stay: the next thing a parent does is enter the other one.
                onSuccess: () => (alone ? navigate(`/events/${event.id}`) : undefined),
              })
            }
            disabled={busy || blocked}
          >
            {join.isPending ? "Registering…" : childName ? `Enter ${childName}` : "Register"}
          </Button>
        </>
      )}

      {failure ? <Notice tone="attention">{failure.message}</Notice> : null}
    </section>
  );
}

/**
 * The note field itself.
 *
 * The hint says who reads it, at the moment of typing, because that is the only place saying
 * it changes what anyone writes. It is the truth of the schema too: `event_notes` hands the
 * note to its author and to organisers — and for a child, to their guardians — and to nobody
 * else.
 */
function NoteField({
  value,
  onChange,
  forChild = false,
}: {
  value: string;
  onChange: (next: string) => void;
  forChild?: boolean;
}) {
  return (
    <label className="mb-4 block">
      <span className="field-label">Anything the organiser should know</span>
      <textarea
        value={value}
        onChange={(event) => onChange(event.target.value)}
        maxLength={NOTE_LIMIT}
        placeholder="Only available in the morning"
        style={{ ...inputStyle, minHeight: 72, padding: "12px" }}
      />
      <span className="mt-1.5 block text-sm text-ink-muted">
        {forChild
          ? "Optional. Only their guardians and the organisers can read it."
          : "Optional. Only you and the organisers can read it."}
        {value.length > NOTE_LIMIT - 60 ? ` ${NOTE_LIMIT - value.length} left.` : ""}
      </span>
    </label>
  );
}
